import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import muiTheme from "./styles/muiTheme";
import { lightTheme } from "./styles/theme";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import SchoolSignup from "./pages/SchoolSignup";
import ViewNotices from "./pages/ViewNotices";
import Profile from "./pages/Profile";
import ForgotPassword from "./pages/ForgotPassword";
import NoticeDetails from "./pages/NoticeDetails";
import Dashboard from "./pages/Dashboard";
import UserManagement from "./pages/UserManagement";
import UserHomePage from "./pages/UserHomePage";
import TeamManagement from "./pages/TeamManagement";
import Circles from "./pages/Circles";
import CircleHomePage, {
  CircleMembersLegacyRedirect,
} from "./pages/CircleHomePage";
import { AuthProvider } from "./context/AuthContext";
import { PageActionsProvider } from "./context/PageActionsContext";
import { SnackbarProvider } from "./context/SnackbarContext";
import ProtectedRoute from "./components/ProtectedRoute";
import RouteLazyFallback from "./components/shared/RouteLazyFallback";
import PlannersSkeleton, { PlannerSkeleton } from "./components/Planners/PlannersSkeleton";
import CalendarSkeleton from "./components/CalendarSkeleton";
import DriveSkeleton from "./components/DriveSkeleton";
import CreateNoticeSkeleton from "./components/CreateNotice/CreateNoticeSkeleton";
import LibrarySkeleton from "./components/Library/LibrarySkeleton";
import SignaturesSkeleton from "./components/Signatures/SignaturesSkeleton";
import NoticeTemplateSkeleton, {
  NEW_NOTICE_TEMPLATE_TITLE,
} from "./components/NoticeTemplates/NoticeTemplateSkeleton";
import {
  AwardRouteSkeleton,
  AwardsSkeleton,
  CertificateTemplateRouteSkeleton,
  CertificateTemplatesSkeleton,
  CertificatesHomeSkeleton,
  NewAwardRouteSkeleton,
  NewCertificateTemplateRouteSkeleton,
} from "./components/Certificates/CertificatesRouteSkeletons";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Brochure from "./pages/Brochure";
import NoticeboardHelp from "./pages/help/NoticeboardHelp";
import NoticesHelp from "./pages/help/NoticesHelp";
import CalendarHelp from "./pages/help/CalendarHelp";
import CirclesHelp from "./pages/help/CirclesHelp";
import VideosHelp from "./pages/help/VideosHelp";
import CreatePlanCourse from "./pages/help/CreatePlanCourse";
import CourseHome from "./pages/help/CourseHome";
import CourseLesson from "./pages/help/CourseLesson";
import NotFound from "./pages/NotFound";
import LoginAuthHelp from "./pages/help/LoginAuthHelp";
import CreateSystemTemplate from "./pages/CreateSystemTemplate";

const NewImageNoticePage = React.lazy(() => import("./pages/images/NewImageNoticePage"));
const Calendar = lazy(() => import("./pages/Calendar"));
const PlannersPage = lazy(() => import("./pages/planners/PlannersPage"));
const PlannerPage = lazy(() => import("./pages/planners/PlannerPage"));
const Drive = lazy(() => import("./pages/Drive"));
const NovaPage = lazy(() => import("./pages/nova/NovaPage"));
const CreateNotice = lazy(() => import("./pages/CreateNotice"));
const NoticeTemplatePage = lazy(
  () => import("./pages/templates/NoticeTemplatePage")
);
const NewNoticeTemplatePage = lazy(
  () => import("./pages/templates/NewNoticeTemplatePage")
);
const CertificatesHome = lazy(
  () => import("./pages/certificates/CertificatesHome")
);
const CertificateTemplatesPage = lazy(
  () => import("./pages/certificates/CertificateTemplatesPage")
);
const AwardsPage = lazy(() => import("./pages/certificates/AwardsPage"));
const NewAwardPage = lazy(() => import("./pages/certificates/NewAwardPage"));
const AwardPage = lazy(() => import("./pages/certificates/AwardPage"));
const NewBirthdayPage = lazy(
  () => import("./pages/birthdays/NewBirthdayPage")
);
const SignaturesPage = lazy(
  () => import("./pages/signatures/SignaturesPage")
);
const CertificateTemplatePage = lazy(
  () => import("./pages/certificates/CertificateTemplatePage")
);
const NewCertificateTemplatePage = lazy(
  () => import("./pages/certificates/NewCertificateTemplatePage")
);
const AdCenter = lazy(() => import("./pages/AdCenter"));
const AIWorksheetGenerator = lazy(
  () => import("./pages/learning-center/AIWorksheetGenerator")
);
const WorksheetsHome = lazy(
  () => import("./pages/learning-center/WorksheetsHome")
);
const WorksheetsGrade = lazy(
  () => import("./pages/learning-center/WorksheetsGrade")
);
const WorksheetBuilderEntry = lazy(
  () => import("./pages/learning-center/WorksheetBuilderEntry")
);
const Library = lazy(() => import("./pages/Library"));
const Resources = lazy(() => import("./pages/Resources"));
const SundaySchoolResources = lazy(
  () => import("./pages/resources/SundaySchoolResources")
);
const ThemeColorsKitchenSink = lazy(
  () => import("./pages/ThemeColorsKitchenSink")
);

// Library and Resources show one or two cards side by side, not three.
const LIBRARY_CARD_GRID_SX = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
  gap: 2,
  maxWidth: 900,
} as const;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
      retry: 1,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <MuiThemeProvider theme={muiTheme}>
      <ThemeProvider theme={lightTheme}>
        <AuthProvider>
          <PageActionsProvider>
            <SnackbarProvider>
              <Router>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/school-signup" element={<SchoolSignup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/brochure" element={<Brochure />} />
                <Route
                  path="/*"
                  element={
                    <Layout>
                      <Suspense fallback={<RouteLazyFallback />}>
                        <Routes>
                        <Route
                          path="/"
                          element={
                            <ProtectedRoute>
                              <Dashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/view-notices"
                          element={
                            <ProtectedRoute requireApproval={true}>
                              <ViewNotices />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/circles/:subcircleId/members"
                          element={
                            <ProtectedRoute
                              requireApproval={true}
                              requiredTeamRoles={["Admin", "Owner"]}
                              requiredFeatureKey="circleManagement"
                            >
                              <CircleMembersLegacyRedirect />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/circles/:subcircleId"
                          element={
                            <ProtectedRoute
                              requireApproval={true}
                              requiredTeamRoles={["Admin", "Owner"]}
                              requiredFeatureKey="circleManagement"
                            >
                              <CircleHomePage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/circles"
                          element={
                            <ProtectedRoute
                              requireApproval={true}
                              requiredTeamRoles={["Admin", "Owner"]}
                              requiredFeatureKey="circleManagement"
                            >
                              <Circles />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/calendar"
                          element={
                            <ProtectedRoute requireApproval={true}>
                              <Suspense fallback={<CalendarSkeleton />}>
                                <Calendar />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/calendar/planners"
                          element={
                            <ProtectedRoute requireApproval={true} requiredFeatureKey="planner" featureOffRedirectTo="/calendar">
                              <Suspense fallback={<PlannersSkeleton />}>
                                <PlannersPage />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/calendar/planners/new"
                          element={
                            <ProtectedRoute requireApproval={true} requiredFeatureKey="planner" featureOffRedirectTo="/calendar">
                              <Suspense fallback={<PlannerSkeleton />}>
                                <PlannerPage />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/calendar/planners/:plannerId"
                          element={
                            <ProtectedRoute requireApproval={true} requiredFeatureKey="planner" featureOffRedirectTo="/calendar">
                              <Suspense fallback={<PlannerSkeleton />}>
                                <PlannerPage />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/nova"
                          element={
                            <ProtectedRoute requireApproval={true} requiredFeatureKey="nova">
                              <Suspense fallback={null}>
                                <NovaPage />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/schedules"
                          element={<Navigate to="/view-notices" replace />}
                        />
                        <Route
                          path="/drive"
                          element={
                            <ProtectedRoute requireApproval={true}>
                              <Suspense fallback={<DriveSkeleton />}>
                                <Drive />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/create-notice"
                          element={
                            <ProtectedRoute requireApproval={true}>
                              <Suspense fallback={<CreateNoticeSkeleton />}>
                                <CreateNotice />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route path="/create-notice/images/new" element={<ProtectedRoute requireApproval={true} requiredFeatureKey="imageStudio"><Suspense fallback={<CreateNoticeSkeleton />}><NewImageNoticePage /></Suspense></ProtectedRoute>} />
                        <Route
                          path="/create-notice/templates/new"
                          element={
                            <ProtectedRoute requireApproval={true}>
                              <Suspense
                                fallback={
                                  <NoticeTemplateSkeleton title={NEW_NOTICE_TEMPLATE_TITLE} />
                                }
                              >
                                <NewNoticeTemplatePage />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/create-notice/templates/:templateId"
                          element={
                            <ProtectedRoute requireApproval={true}>
                              <Suspense fallback={<NoticeTemplateSkeleton />}>
                                <NoticeTemplatePage />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/create-notice/celebrations/new"
                          element={
                            <ProtectedRoute requireApproval={true}>
                              <Suspense fallback={<RouteLazyFallback />}>
                                <NewBirthdayPage />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/create-notice/birthdays/new"
                          element={
                            <ProtectedRoute requireApproval={true}>
                              <Suspense fallback={<RouteLazyFallback />}>
                                <NewBirthdayPage />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/create-notice/signatures"
                          element={
                            <ProtectedRoute requireApproval={true}>
                              <Suspense fallback={<SignaturesSkeleton />}>
                                <SignaturesPage />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/create-notice/certificates"
                          element={
                            <ProtectedRoute requireApproval={true}>
                              <Suspense fallback={<CertificatesHomeSkeleton />}>
                                <CertificatesHome />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/create-notice/certificates/templates"
                          element={
                            <ProtectedRoute requireApproval={true}>
                              <Suspense fallback={<CertificateTemplatesSkeleton />}>
                                <CertificateTemplatesPage />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/create-notice/certificates/awards"
                          element={
                            <ProtectedRoute requireApproval={true}>
                              <Suspense fallback={<AwardsSkeleton />}>
                                <AwardsPage />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/create-notice/certificates/awards/new"
                          element={
                            <ProtectedRoute requireApproval={true}>
                              <Suspense fallback={<NewAwardRouteSkeleton />}>
                                <NewAwardPage />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/create-notice/certificates/awards/:awardId/:tab?"
                          element={
                            <ProtectedRoute requireApproval={true}>
                              <Suspense fallback={<AwardRouteSkeleton />}>
                                <AwardPage />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/create-notice/certificates/templates/new"
                          element={
                            <ProtectedRoute requireApproval={true}>
                              <Suspense fallback={<NewCertificateTemplateRouteSkeleton />}>
                                <NewCertificateTemplatePage />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/create-notice/certificates/templates/:certificateTemplateId"
                          element={
                            <ProtectedRoute requireApproval={true}>
                              <Suspense fallback={<CertificateTemplateRouteSkeleton />}>
                                <CertificateTemplatePage />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />

                        <Route
                          path="/profile"
                          element={
                            <ProtectedRoute>
                              <Profile />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/user-management/:userId"
                          element={
                            <ProtectedRoute
                              requireApproval={true}
                              requiredTeamRoles={["Admin"]}
                              requiredFeatureKey="userManagement"
                            >
                              <UserHomePage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/user-management"
                          element={
                            <ProtectedRoute
                              requireApproval={true}
                              requiredTeamRoles={["Admin"]}
                              requiredFeatureKey="userManagement"
                            >
                              <UserManagement />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/team-management"
                          element={
                            <ProtectedRoute
                              requireApproval={true}
                              requiredTeamRoles={["Admin", "Owner"]}
                              requiredFeatureKey="teamManagement"
                            >
                              <TeamManagement />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/help/noticeboard"
                          element={
                            <ProtectedRoute>
                              <NoticeboardHelp />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/help/notices"
                          element={
                            <ProtectedRoute>
                              <NoticesHelp />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/help/calendar"
                          element={
                            <ProtectedRoute>
                              <CalendarHelp />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/help/circles"
                          element={
                            <ProtectedRoute>
                              <CirclesHelp />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/help/videos"
                          element={
                            <ProtectedRoute>
                              <VideosHelp />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/help/courses"
                          element={
                            <ProtectedRoute>
                              <CourseHome />
                            </ProtectedRoute>
                          }
                        />
                        <Route path="/help/courses/create-and-plan" element={<ProtectedRoute><CreatePlanCourse /></ProtectedRoute>} />
                        <Route path="/help/courses/create-and-plan/:slug" element={<ProtectedRoute><CreatePlanCourse /></ProtectedRoute>} />
                        <Route
                          path="/help/courses/:slug"
                          element={
                            <ProtectedRoute>
                              <CourseLesson />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/help/login-auth"
                          element={
                            <ProtectedRoute>
                              <LoginAuthHelp />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/notice/:id"
                          element={
                            <ProtectedRoute requireApproval={true}>
                              <NoticeDetails />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/create-template"
                          element={
                            <ProtectedRoute requireApproval={false}>
                              <CreateSystemTemplate />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/ad-center"
                          element={
                            <ProtectedRoute
                              requireApproval={true}
                              requiredTeamRoles={["Admin"]}
                              requiredFeatureKey="adCenter"
                            >
                              <AdCenter />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/worksheet-generator"
                          element={
                            <ProtectedRoute requireApproval={true} requiredFeatureKey="ai">
                              <AIWorksheetGenerator />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/library"
                          element={
                            <ProtectedRoute
                              requireApproval={true}
                              requiredSchoolTypes={["school", "church"]}
                            >
                              <Suspense fallback={<LibrarySkeleton label="Loading Library" top="hero" cards={2} gridSx={LIBRARY_CARD_GRID_SX} />}>
                                <Library />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/create/worksheets"
                          element={
                            <ProtectedRoute
                              requireApproval={true}
                              requiredFeatureKey="ai"
                              requiredSchoolTypes={["school"]}
                            >
                              <Suspense fallback={<LibrarySkeleton label="Loading Library" top="hero" />}>
                                <WorksheetsHome />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/create/worksheets/:gradeKey"
                          element={
                            <ProtectedRoute
                              requireApproval={true}
                              requiredFeatureKey="ai"
                              requiredSchoolTypes={["school"]}
                            >
                              <Suspense fallback={<LibrarySkeleton label="Loading worksheets" top="title" />}>
                                <WorksheetsGrade />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/create/worksheets/:gradeKey/:worksheetType"
                          element={
                            <ProtectedRoute
                              requireApproval={true}
                              requiredFeatureKey="ai"
                              requiredSchoolTypes={["school"]}
                            >
                              <Suspense fallback={<LibrarySkeleton label="Loading worksheets" top="title" />}>
                                <WorksheetBuilderEntry />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/resources"
                          element={
                            <ProtectedRoute
                              requireApproval={true}
                              requiredSchoolTypes={["church"]}
                            >
                              <Suspense fallback={<LibrarySkeleton label="Loading Resources" top="hero" cards={2} gridSx={LIBRARY_CARD_GRID_SX} />}>
                                <Resources />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/resources/sunday-school"
                          element={
                            <ProtectedRoute
                              requireApproval={true}
                              requiredSchoolTypes={["church"]}
                            >
                              <Suspense fallback={<LibrarySkeleton label="Loading stories" top="title" />}>
                                <SundaySchoolResources />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/kitchen-sink/colors"
                          element={
                            process.env.NODE_ENV !== "production" ? (
                              <ThemeColorsKitchenSink />
                            ) : (
                              <NotFound />
                            )
                          }
                        />
                        <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Suspense>
                    </Layout>
                  }
                />
              </Routes>
              </Router>
            </SnackbarProvider>
          </PageActionsProvider>
        </AuthProvider>
      </ThemeProvider>
    </MuiThemeProvider>
  </QueryClientProvider>
);

export default App;
